import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, Download, Eye, Layers3, MapPin, Radio, RefreshCw, SquareArrowUp, ShieldCheck, TriangleAlert, UsersRound } from 'lucide-react';
import QrScannerPanel from './components/QrScannerPanel.jsx';
import SessionPanel from './components/SessionPanel.jsx';
import CommandIcon from './components/CommandIcon.jsx';
import SettingsDialog from './components/SettingsDialog.jsx';
import VoiceRecorder from './components/VoiceRecorder.jsx';
import TextMessenger from './components/TextMessenger.jsx';
import { loadPreferences, normalizePreferences, PREFERENCES_KEY } from './lib/preferences.js';
import { visibleFeatures, visibleTeams, visibleTrackers } from './lib/session.js';
import { getInstallEnvironment } from './lib/install.js';
import {
  buildShareUrl,
  fetchLiveSnapshot,
  isShareCredentials,
  readShareFromLocation
} from './lib/share.js';
import {
  detectLanguage,
  formatRelativeTime,
  normalizeLanguage,
  translate
} from './i18n.js';

const MapView = lazy(() => import('./components/MapView.jsx'));

const SESSION_STORAGE_KEY = 'cynoops.live.share';
const POLL_INTERVAL_MS = 4000;
const BACKOFF_INTERVAL_MS = 10000;
const ENDED_SESSION_POLL_INTERVAL_MS = 60000;

const loadStoredCredentials = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || 'null');
    return isShareCredentials(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const persistCredentials = (credentials) => {
  try { sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(credentials)); } catch {}
  try { history.replaceState(null, '', buildShareUrl(credentials, location.origin)); } catch {}
};

const clearCredentials = () => {
  try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch {}
  try { history.replaceState(null, '', '/'); } catch {}
};

const errorTranslationKey = (code) => {
  const normalized = String(code || '').toLowerCase();
  if (normalized.includes('expired')) return 'error.expired';
  if (normalized.includes('revoked')) return 'error.revoked';
  if (normalized.includes('session-gone') || normalized.includes('not-found') || normalized.includes('missing')) return 'error.missing';
  if (normalized.includes('invalid') || normalized.includes('unauth')) return 'error.invalid';
  if (normalized.includes('network') || normalized.includes('unavailable')) return 'error.network';
  return 'error.generic';
};

function BrandLockup({ t }) {
  return (
    <div className="brand-lockup" aria-label="CYNOOPS LIVE">
      <img className="brand-mark" src="/command-mark.webp" alt="" aria-hidden="true" />
      <span className="brand-name">CYNOOPS <strong>{t('app.title')}</strong></span>
    </div>
  );
}

function ConfigButton({ onOpenSettings, t }) {
  return <button type="button" className="icon-button config-button" onClick={onOpenSettings}
    aria-label={t('settings.title')} title={t('settings.title')} aria-haspopup="dialog" data-testid="config-button"><CommandIcon name="gear" /></button>;
}

function AppHeader({ installButtonRef, onInstall, onOpenSettings, showInstall = false, showTagline = true, t }) {
  return (
    <header className={`app-header${showInstall ? ' app-header--installable' : ''}`}>
      <BrandLockup t={t} />
      {showTagline && <p className="brand-tagline">{t('app.tagline')}</p>}
      <div className="app-header__actions">
        {showInstall && (
          <button
            ref={installButtonRef}
            type="button"
            className="icon-button install-app-button"
            onClick={onInstall}
            aria-label={t('install.action')}
            title={t('install.action')}
            data-testid="install-app-button"
          >
            <Download aria-hidden="true" />
          </button>
        )}
        <ConfigButton onOpenSettings={onOpenSettings} t={t} />
      </div>
    </header>
  );
}

function InstallHelpDialog({ buttonRef, onClose, t }) {
  return (
    <div
      className="confirm-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="confirm-dialog install-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-title"
        aria-describedby="install-description"
        data-testid="install-help-dialog"
      >
        <span className="confirm-dialog__icon" aria-hidden="true"><SquareArrowUp /></span>
        <div className="confirm-dialog__copy">
          <h2 id="install-title">{t('install.title')}</h2>
          <p id="install-description">{t('install.ios')}</p>
        </div>
        <div className="confirm-dialog__actions">
          <button ref={buttonRef} type="button" className="button button--primary" onClick={onClose} autoFocus>
            {t('install.done')}
          </button>
        </div>
      </section>
    </div>
  );
}

function LoadingView({ t }) {
  return (
    <main className="state-page" data-testid="loading-state">
      <section className="state-card">
        <span className="state-icon state-icon--loading"><Radio aria-hidden="true" /></span>
        <span className="spinner spinner--large" aria-hidden="true" />
        <h1>{t('loading.title')}</h1>
        <p>{t('loading.body')}</p>
      </section>
    </main>
  );
}

function ErrorView({ errorKey, onRetry, onReset, t }) {
  return (
    <main className="state-page" data-testid="error-state">
      <section className="state-card state-card--error">
        <span className="state-icon"><MapPin aria-hidden="true" /></span>
        <h1>{t('error.title')}</h1>
        <p>{t(errorKey)}</p>
        <div className="state-actions">
          <button className="button button--primary" type="button" onClick={onRetry}>
            <RefreshCw aria-hidden="true" />{t('error.retry')}
          </button>
          <button className="button" type="button" onClick={onReset}>
            <ChevronLeft aria-hidden="true" />{t('app.newSession')}
          </button>
        </div>
      </section>
    </main>
  );
}

function Viewer({ snapshot, credentials, stale, language, preferences, onPreferencesChange, onOpenSettings, onReset, t }) {
  const styleKey = preferences.mapStyle;
  const [featuresOpen, setFeaturesOpen] = useState(true);
  const [teamsOpen, setTeamsOpen] = useState(true);
  const [compact, setCompact] = useState(() => matchMedia('(max-width: 1100px)').matches);
  useEffect(() => {
    const query = matchMedia('(max-width: 1100px)');
    const update = () => setCompact(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const [activeTab, setActiveTab] = useState('teams');
  const [selected, setSelected] = useState(null);
  const [focusRequest, setFocusRequest] = useState(null);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [fitSignal, setFitSignal] = useState(1);
  const [mapAction, setMapAction] = useState(null);
  const runMapAction = (action) => setMapAction((current) => ({ action, nonce: (current?.nonce || 0) + 1 }));
  const [, setClock] = useState(0);
  const closeButtonRef = useRef(null);
  const closeDialogRef = useRef(null);
  const contentRef = useRef(null);
  const session = snapshot.session || {};
  const ended = Boolean(session.endAt) || session.status === 'ended';

  useEffect(() => {
    const content = contentRef.current, panel = content?.querySelector('.info-panel--teams');
    if (!content) return undefined;
    const update = () => content.style.setProperty('--live-bottom-panel-height', `${panel?.getBoundingClientRect().height || 0}px`);
    update();
    if (!panel || !globalThis.ResizeObserver) return undefined;
    const observer = new ResizeObserver(update); observer.observe(panel);
    return () => observer.disconnect();
  }, [teamsOpen, compact]);

  useEffect(() => {
    const timer = setInterval(() => setClock((value) => value + 1), 15000);
    return () => clearInterval(timer);
  }, []);

  const selectMapItem = useCallback((value) => {
    if (!value?.type || !value?.id) return;
    setSelected(value);
    setActiveTab(value.type === 'tracker' ? 'trackers' : value.type === 'team' ? 'teams' : 'features');
    setTeamsOpen(true);
    if (value.type === 'feature') setFeaturesOpen(true);
    setFocusRequest((current) => ({
      type: value.type,
      id: String(value.id),
      nonce: (current?.nonce || 0) + 1
    }));
  }, []);

  const dismissCloseConfirmation = useCallback(() => {
    setCloseConfirmOpen(false);
    requestAnimationFrame(() => closeButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!closeConfirmOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        dismissCloseConfirmation();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(closeDialogRef.current?.querySelectorAll(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
      ) || []);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeConfirmOpen, dismissCloseConfirmation]);

  return (
    <main className={`viewer${featuresOpen ? '' : ' viewer--features-closed'}${teamsOpen ? '' : ' viewer--teams-closed'}`} data-testid="viewer">
      <header className="viewer-toolbar" inert={closeConfirmOpen || messageDialogOpen ? '' : undefined} aria-hidden={closeConfirmOpen || messageDialogOpen ? 'true' : undefined}>
        <div className="viewer-controls" role="toolbar" aria-label={t('viewer.navigation')}>
          <div className="viewer-tool-group viewer-tool-group--navigation">
            <span className="tool-group-label">{t('viewer.navigation')}</span>
            {[
              ['zoomIn', 'plus'], ['zoomOut', 'minus'], ['resetNorth', 'compass']
            ].map(([action, icon]) => <button key={action} type="button" className="icon-button" onClick={() => runMapAction(action)}
              aria-label={t(`viewer.${action}`)} title={t(`viewer.${action}`)}><CommandIcon name={icon} /></button>)}
            <button type="button" className="icon-button" onClick={() => setFitSignal((value) => value + 1)} aria-label={t('viewer.fit')} title={t('viewer.fit')}><CommandIcon name="crosshair" /></button>
          </div>
          <div className="viewer-tool-group viewer-tool-group--panels">
            <span className="tool-group-label">{t('viewer.view')}</span>
            <button type="button" className="icon-button" aria-pressed={compact ? teamsOpen && activeTab === 'features' : featuresOpen}
              onClick={() => { if (compact) { setActiveTab('features'); setTeamsOpen(true); } else setFeaturesOpen((value) => !value); }}
              aria-label={t('viewer.features')} title={t('viewer.features')} data-testid="features-panel-toggle"><CommandIcon name="polygon" /></button>
            <button type="button" className="icon-button" onClick={() => setTeamsOpen((value) => !value)} aria-pressed={teamsOpen}
              aria-label={teamsOpen ? t('viewer.hidePanel') : t('viewer.showPanel')} title={t('viewer.teams')} data-testid="panel-toggle"><CommandIcon name="air-traffic-control" /></button>
          </div>
          <div className="viewer-tool-group viewer-tool-group--map" role="group" aria-label={t('viewer.mapStyle')} data-testid="map-style-buttons">
            <span className="tool-group-label">{t('viewer.mapStyle')}</span>
            {[['street', 'road-horizon'], ['terrain', 'mountains'], ['satellite', 'map-trifold']].map(([style, icon]) => (
              <button key={style} type="button" className="icon-button map-style-button" aria-pressed={styleKey === style}
                onClick={() => onPreferencesChange({ mapStyle: style })} aria-label={t(`viewer.${style}`)} title={t(`viewer.${style}`)} data-testid={`map-style-${style}`}><CommandIcon name={icon} /></button>
            ))}
          </div>
        </div>
        <div className="viewer-title">
          <span className={`live-pulse${ended ? ' live-pulse--ended' : ''}`}><i /></span>
          <div><h1>{session.title && session.title !== session.id ? session.title : 'CYNOOPS LIVE'}</h1></div>
        </div>
        <div className="viewer-read-only"><ShieldCheck aria-hidden="true" />{t('app.readOnly')}</div>
        <div className="viewer-toolbar-actions">
          <ConfigButton onOpenSettings={onOpenSettings} t={t} />
          <button ref={closeButtonRef} type="button" className="icon-button close-session-button" onClick={() => setCloseConfirmOpen(true)}
            aria-label={t('viewer.closeView')} title={t('viewer.closeView')} data-testid="close-session-button"><CommandIcon name="x" /></button>
        </div>
      </header>
      <div
        ref={contentRef}
        className="viewer-content"
        inert={closeConfirmOpen || messageDialogOpen ? '' : undefined}
        aria-hidden={closeConfirmOpen || messageDialogOpen ? 'true' : undefined}
      >
        {featuresOpen && <SessionPanel onCollapse={() => setFeaturesOpen(false)} snapshot={snapshot} side="features" language={language} activeTab="features" selected={selected} onSelect={selectMapItem} t={t} />}
        <Suspense fallback={(
          <div className="map-fallback" role="status">
            <span className="spinner" aria-hidden="true" />
            {t('viewer.mapLoading')}
          </div>
        )}>
          <MapView
            snapshot={snapshot}
            preferences={preferences}
            language={language}
            styleKey={styleKey}
            fitSignal={fitSignal}
            mapAction={mapAction}
            selected={selected}
            focusRequest={focusRequest}
            onSelect={selectMapItem}
            t={t}
          />
        </Suspense>
        {teamsOpen && (
          <SessionPanel
            onCollapse={() => setTeamsOpen(false)}
            side="teams"
            snapshot={snapshot}
            activeTab={activeTab === 'features' && !compact ? 'teams' : activeTab}
            onTabChange={setActiveTab}
            selected={selected}
            onSelect={selectMapItem}
            language={language}
            t={t}
          />
        )}
        <div className="live-message-launcher" role="group" aria-label={t('text.actions')}>
          <TextMessenger credentials={credentials} ended={ended} onOpenChange={setMessageDialogOpen} t={t} />
          <VoiceRecorder credentials={credentials} ended={ended} onOpenChange={setMessageDialogOpen} t={t} />
        </div>
      </div>
      <footer className="viewer-footer" inert={closeConfirmOpen || messageDialogOpen ? '' : undefined} aria-hidden={closeConfirmOpen || messageDialogOpen ? 'true' : undefined}>
        <span className="viewer-footer-brand"><img src="/command-mark.webp" alt="" />LIVE</span>
        <span className="viewer-footer__session" title={session.id}><Radio aria-hidden="true" />{t('viewer.sessionId')}: <strong>{session.id}</strong></span>
        <span><Layers3 aria-hidden="true" />{visibleFeatures(session).length} {t('viewer.features')}</span>
        <span><UsersRound aria-hidden="true" />{visibleTeams(session).length} {t('viewer.teams')}</span>
        <span><Radio aria-hidden="true" />{visibleTrackers(snapshot).length} {t('viewer.trackers')}</span>
        <span className={`viewer-footer__status${stale ? ' is-stale' : ''}`} role="status">{stale ? t('viewer.stale') : t('viewer.updated', { time: formatRelativeTime(language, snapshot.generatedAt) })}</span>
      </footer>
      {closeConfirmOpen && (
        <div
          className="confirm-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) dismissCloseConfirmation();
          }}
        >
          <section
            ref={closeDialogRef}
            className="confirm-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="close-session-title"
            aria-describedby="close-session-description"
            data-testid="close-session-dialog"
          >
            <span className="confirm-dialog__icon" aria-hidden="true"><TriangleAlert /></span>
            <div className="confirm-dialog__copy">
              <h2 id="close-session-title">{t('viewer.closeConfirmTitle')}</h2>
              <p id="close-session-description">{t('viewer.closeConfirmBody')}</p>
            </div>
            <div className="confirm-dialog__actions">
              <button
                type="button"
                className="button"
                onClick={dismissCloseConfirmation}
                data-testid="close-session-cancel"
                autoFocus
              >
                {t('viewer.closeConfirmCancel')}
              </button>
              <button
                type="button"
                className="button button--danger"
                onClick={onReset}
                data-testid="close-session-confirm"
              >
                {t('viewer.closeConfirmAction')}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

export default function App() {
  const [language, setLanguage] = useState(detectLanguage);
  const [preferences, setPreferences] = useState(loadPreferences);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const handlePreferencesChange = useCallback((patch) => setPreferences((current) => normalizePreferences({ ...current, ...patch })), []);
  const openSettings = useCallback((event) => { settingsTriggerRef.current = event.currentTarget; setSettingsOpen(true); }, []);
  const closeSettings = useCallback(() => { setSettingsOpen(false); requestAnimationFrame(() => settingsTriggerRef.current?.focus()); }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = preferences.theme;
    try { localStorage.setItem(PREFERENCES_KEY, JSON.stringify(preferences)); } catch {}
  }, [preferences]);
  const [credentials, setCredentials] = useState(() => readShareFromLocation() || loadStoredCredentials());
  const [snapshot, setSnapshot] = useState(null);
  const snapshotRef = useRef(null);
  const installPromptRef = useRef(null);
  const installButtonRef = useRef(null);
  const installDialogButtonRef = useRef(null);
  const installEnvironment = useMemo(() => getInstallEnvironment(), []);
  const [phase, setPhase] = useState(() => credentials ? 'loading' : 'scan');
  const [errorKey, setErrorKey] = useState('error.generic');
  const [stale, setStale] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [canPromptInstall, setCanPromptInstall] = useState(false);
  const [installed, setInstalled] = useState(installEnvironment.standalone);
  const [installHelpOpen, setInstallHelpOpen] = useState(false);

  const t = useCallback((key, replacements) => translate(language, key, replacements), [language]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.title = `CYNOOPS ${translate(language, 'app.title')}`;
    try { localStorage.setItem('cynoops.live.language', language); } catch {}
  }, [language]);

  useEffect(() => {
    if (installed) return undefined;
    const handleBeforeInstallPrompt = (event) => {
      if (phase !== 'scan') return;
      event.preventDefault();
      installPromptRef.current = event;
      setCanPromptInstall(true);
    };
    const handleInstalled = () => {
      installPromptRef.current = null;
      setCanPromptInstall(false);
      setInstallHelpOpen(false);
      setInstalled(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, [installed, phase]);

  const dismissInstallHelp = useCallback(() => {
    setInstallHelpOpen(false);
    requestAnimationFrame(() => installButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!installHelpOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') dismissInstallHelp();
      if (event.key === 'Tab') {
        event.preventDefault();
        installDialogButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dismissInstallHelp, installHelpOpen]);

  const handleInstall = useCallback(async () => {
    const promptEvent = installPromptRef.current;
    if (promptEvent) {
      installPromptRef.current = null;
      setCanPromptInstall(false);
      try {
        await promptEvent.prompt();
        await promptEvent.userChoice;
      } catch {}
      return;
    }
    if (installEnvironment.ios) setInstallHelpOpen(true);
  }, [installEnvironment.ios]);

  const handleLanguageChange = useCallback((value) => setLanguage(normalizeLanguage(value)), []);

  const handleCredentials = useCallback((value) => {
    if (!isShareCredentials(value)) return;
    persistCredentials(value);
    setCredentials(value);
    setSnapshot(null);
    snapshotRef.current = null;
    setStale(false);
    setPhase('loading');
  }, []);

  useEffect(() => {
    const handleLocation = () => {
      const next = readShareFromLocation();
      if (next && (next.tokenId !== credentials?.tokenId || next.token !== credentials?.token)) handleCredentials(next);
    };
    window.addEventListener('hashchange', handleLocation);
    window.addEventListener('popstate', handleLocation);
    return () => {
      window.removeEventListener('hashchange', handleLocation);
      window.removeEventListener('popstate', handleLocation);
    };
  }, [credentials, handleCredentials]);

  const resetSession = useCallback(() => {
    clearCredentials();
    setCredentials(null);
    setSnapshot(null);
    snapshotRef.current = null;
    setStale(false);
    setPhase('scan');
  }, []);

  useEffect(() => {
    if (!credentials) return undefined;
    persistCredentials(credentials);
    let stopped = false;
    let timer = null;
    let controller = null;

    const schedule = (delay) => {
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(() => { void refresh(); }, delay);
    };

    const refresh = async () => {
      if (stopped) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && snapshotRef.current) {
        schedule(BACKOFF_INTERVAL_MS);
        return;
      }
      controller?.abort();
      controller = new AbortController();
      try {
        const next = await fetchLiveSnapshot(credentials, { signal: controller.signal });
        if (stopped) return;
        snapshotRef.current = next;
        setSnapshot(next);
        setStale(false);
        setPhase('view');
        schedule(next?.session?.status === 'ended' ? ENDED_SESSION_POLL_INTERVAL_MS : POLL_INTERVAL_MS);
      } catch (error) {
        if (stopped || error?.name === 'AbortError') return;
        const key = errorTranslationKey(error?.code);
        const transient = key === 'error.network' || error?.status >= 500;
        if (snapshotRef.current && transient) {
          setStale(true);
          schedule(BACKOFF_INTERVAL_MS);
        } else {
          setErrorKey(key);
          setPhase('error');
        }
      }
    };

    const handleOnline = () => {
      if (snapshotRef.current) setStale(false);
      void refresh();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    void refresh();
    return () => {
      stopped = true;
      clearTimeout(timer);
      controller?.abort();
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [credentials, retryNonce]);

  const content = useMemo(() => {
    if (phase === 'scan') return <QrScannerPanel onCredentials={handleCredentials} t={t} />;
    if (phase === 'loading') return <LoadingView t={t} />;
    if (phase === 'error') {
      return (
        <ErrorView
          errorKey={errorKey}
          onRetry={() => { setPhase('loading'); setRetryNonce((value) => value + 1); }}
          onReset={resetSession}
          t={t}
        />
      );
    }
    if (phase === 'view' && snapshot) {
      return (
        <Viewer
          snapshot={snapshot}
          credentials={credentials}
          stale={stale}
          language={language}
          preferences={preferences}
          onPreferencesChange={handlePreferencesChange}
          onOpenSettings={openSettings}
          onReset={resetSession}
          t={t}
        />
      );
    }
    return <LoadingView t={t} />;
  }, [credentials, errorKey, handleCredentials, handlePreferencesChange, language, openSettings, phase, preferences, resetSession, snapshot, stale, t]);

  const installAvailable = !installed && (canPromptInstall || installEnvironment.ios);

  return (
    <>
      <div
        className={`live-app live-app--${phase}`}
        inert={installHelpOpen || settingsOpen ? '' : undefined}
        aria-hidden={installHelpOpen || settingsOpen ? 'true' : undefined}
      >
        {phase !== 'view' && (
          <AppHeader
            installButtonRef={installButtonRef}
            onInstall={handleInstall}
            onOpenSettings={openSettings}
            showInstall={phase === 'scan' && installAvailable}
            showTagline={phase !== 'scan'}
            t={t}
          />
        )}
        {content}
        {phase !== 'view' && phase !== 'scan' && (
          <footer className="app-footer">
            <Eye aria-hidden="true" />
            <span>{t('app.about')}</span>
          </footer>
        )}
      </div>
      {settingsOpen && <SettingsDialog language={language} onLanguageChange={handleLanguageChange} preferences={preferences} onChange={handlePreferencesChange} onClose={closeSettings} t={t} />}
      {installHelpOpen && (
        <InstallHelpDialog buttonRef={installDialogButtonRef} onClose={dismissInstallHelp} t={t} />
      )}
    </>
  );
}

// Kept exported for deterministic QR/link round-trip smoke tests.
export const createBrowserShareUrl = (credentials) => buildShareUrl(credentials, globalThis.location?.origin);
